import { useEffect, useRef, useState } from "react";
import styles from "./assets/css/ContactSection.module.css";
import ReCAPTCHA from "react-google-recaptcha";
import useFetch from "../../Hooks/useFetch";

const ContactSection = () => {
  const recaptcha = useRef();
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [number, setNumber] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingError, setSendingError] = useState(false);
  const [sent, setSent] = useState(false);
  const [wpNonce, setWpNonce] = useState(null);

  const {
    data: nonceData,
    loading: nonceLoading,
    error: nonceError,
  } = useFetch("nonce", "wp-json/custom-email-form/v1");

  useEffect(() => {
    if (!nonceData) return;
    setWpNonce(nonceData.nonce);
  }, [nonceData]);

  const submitForm = async (e) => {
    e.preventDefault();
    resetFeedback();
    const formData = new FormData(e.target);

    for (const pair of formData.entries()) {
      pair[1] = pair[1].trim();
    }

    const captchaValue = await recaptcha.current.executeAsync();

    if (!captchaValue) return console.error("Error generating reCAPTCHA");
    setSent(false);
    fetch(
      `${process.env.REACT_APP_API_URI}/wp-json/custom-email-form/v1/submit-form`,
      {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        body: JSON.stringify({
          name,
          company,
          number,
          email,
          message,
          _wpnonce: wpNonce,
          "g-recaptcha-response": captchaValue,
        }),
      }
    )
      .then(async (res) => {
        setSending(false);
        setSent(true);
        const { success, data: message } = await res.json();
        setSendingError(!success);
        if (!success) throw new Error(message);
        resetForm();
      })
      .catch((error) => {
        console.error(error);
        setSendingError(true);
        setSending(false);
      });
    recaptcha.current.reset();
  };

  const resetFeedback = () => {
    setSent(false);
    setSending(false);
    setSendingError(false);
  };

  const resetForm = () => {
    setName("");
    setCompany("");
    setNumber("");
    setEmail("");
    setMessage("");
    setSending(false);
    setSendingError(false);
  };

  return (
    <section className={`${styles.contactSection}`}>
      <div className={styles.sectionHeading}>
        <h1>Contact Us</h1>
      </div>
      <div className={`${styles.contactSectionContainer} `}>
        <div className={styles.detailsContainer}>
          <div className={styles.contactDetails}>
            <div className={styles.address}>
              <h2>Technical Touch Racing</h2>
              <p>European Headquarters</p>
              <p>Technical Touch BV,</p>
              <p>Ondernemersstraat 20, 3920</p>
              <p>Lommel, Belgium</p>
            </div>
            <a href="#location">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="26"
                width="19"
                viewBox="0 0 384 512"
              >
                <path d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
              </svg>
              View Location
            </a>
          </div>
          <div className={styles.contactForm}>
            <h3>Get In Touch</h3>
            <form method="POST" onSubmit={submitForm}>
              <input
                type="text"
                name="name"
                id="name"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <input
                type="text"
                name="company"
                id="company"
                placeholder="Company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required
              />
              <input
                type="text"
                name="number"
                id="number"
                placeholder="Contact no."
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                required
              />
              <input
                type="email"
                name="email"
                id="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <textarea
                name="message"
                id="message"
                cols="30"
                rows="10"
                placeholder="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              ></textarea>
              <ReCAPTCHA
                ref={recaptcha}
                sitekey="6Lf9r18pAAAAAKwz6-Q9CoI5MIx8mDTXDtWqQLlY"
                size="invisible"
              />

              {!sending && sendingError && (
                <p className={styles.errorMessage}>
                  Error Sending Message, Please Try Again!
                </p>
              )}

              {!sending && sent && !sendingError && (
                <p className={styles.successMessage}>
                  Message Sent Successfully
                </p>
              )}

              <button
                type="submit"
                disabled={sending || nonceLoading || nonceError}
                title={
                  nonceLoading
                    ? "Loading required data. Please Wait"
                    : nonceError
                    ? "Error loading required data. Try refreshing the page."
                    : ""
                }
              >
                {nonceLoading ? "Loading" : nonceError ? "Error" : "Submit"}
              </button>
            </form>
          </div>
        </div>
        <div id="location" className={styles.mapContainer}>
          <iframe
            title="google map showing headquarters location"
            src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3497.4422583439496!2d5.276815338193719!3d51.21142536133226!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c72a91066442cf%3A0x9823a2c81d6962c8!2sTechnical%20Touch!5e1!3m2!1sen!2suk!4v1702656264067!5m2!1sen!2suk"
            width="100%"
            style={{ border: 0, margin: 0, padding: 0 }}
            allowFullScreen={true}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
